import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getPinUnlockToken } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useT } from '../lib/i18n';
import { LogOut, Users, Volume2, Camera, KeyRound, Eye, EyeOff, FileText, Pencil, FileCheck2, ChevronRight, BookOpenText, Sparkles, LifeBuoy, HeartHandshake, Lock, Unlock, MapPin, Building2, Phone, User as UserIcon, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import ChildForm from '../components/ChildForm';
import PinGateModal from '../components/PinGateModal';
import Msg91Widget from '../components/Msg91Widget';
import { INDIAN_STATES } from '../lib/indianStates';
import { Link } from 'react-router-dom';

const PIN_REGEX = /^[\x21-\x7E]{4,8}$/;

function PinInput({ value, onChange, placeholder, testId }) {
  const [reveal, setReveal] = useState(false);
  return (
    <div className="relative">
      <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value.slice(0, 8))}
        type={reveal ? 'text' : 'password'} inputMode="text" autoComplete="new-password"
        className="w-full pl-4 pr-12 py-3 rounded-2xl border-2 border-slate-200 focus:border-[#FF6B1A] outline-none text-lg tracking-widest text-center"
        placeholder={placeholder}/>
      <button type="button" data-testid={`${testId}-reveal`} onClick={() => setReveal((r) => !r)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-700"
        aria-label={reveal ? 'Hide PIN' : 'Show PIN'}>
        {reveal ? <EyeOff size={18} strokeWidth={2.5}/> : <Eye size={18} strokeWidth={2.5}/>}
      </button>
    </div>
  );
}

async function fileToDataUrl(file, maxSize = 512) {
  const buf = await file.arrayBuffer();
  const blob = new Blob([buf], { type: file.type });
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  c.getContext('2d').drawImage(bitmap, 0, 0, w, h);
  return c.toDataURL('image/jpeg', 0.85);
}

export default function Profile() {
  const nav = useNavigate();
  const { user, activeChild, updateActiveChild, clearChild, logout, uiLang, changeUiLang, refreshUser } = useApp();
  const t = useT(uiLang);
  const [speed, setSpeed] = useState(activeChild?.tts_speed ?? 0.75);
  const [gender, setGender] = useState(activeChild?.voice_gender ?? 'female');
  const [saving, setSaving] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);

  // Iter-52.32 — Parent contact / location backfill. Existing users
  // who signed up before geo capture landed can add their State,
  // City, and (if missing) Mobile so admin analytics fill in.
  const [parentEditing, setParentEditing] = useState(false);
  const [parentName, setParentName] = useState(user?.name || '');
  const [parentState, setParentState] = useState(user?.state || '');
  const [parentCity, setParentCity] = useState(user?.city || '');
  const [parentSaving, setParentSaving] = useState(false);
  const [addingMobile, setAddingMobile] = useState(false);
  const [mobileWidgetKey, setMobileWidgetKey] = useState(0);
  const [mobileVerifying, setMobileVerifying] = useState(false);
  // Iter-52.41 — hint ack gate (see Register.jsx).
  const [mobileHintAcked, setMobileHintAcked] = useState(false);

  useEffect(() => {
    setParentName(user?.name || '');
    setParentState(user?.state || '');
    setParentCity(user?.city || '');
  }, [user?.name, user?.state, user?.city]);

  const saveParentProfile = async () => {
    const name = (parentName || '').trim();
    if (!name) { toast.error('Please enter your name.'); return; }
    setParentSaving(true);
    try {
      await api.post('/users/update-profile', {
        name,
        state: parentState || '',
        city: (parentCity || '').trim(),
      });
      await refreshUser?.();
      toast.success('Profile updated');
      setParentEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save your profile.');
    } finally {
      setParentSaving(false);
    }
  };

  const onMobileVerified = async (accessToken) => {
    if (mobileVerifying) return;
    setMobileVerifying(true);
    try {
      await api.post('/users/update-mobile', { access_token: accessToken });
      await refreshUser?.();
      toast.success('Mobile number added to your account.');
      setAddingMobile(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not add mobile. Please try again.';
      toast.error(detail);
      setMobileWidgetKey(k => k + 1);
    } finally {
      setMobileVerifying(false);
    }
  };

  const saveChild = async (patch) => {
    setSaving(true);
    try {
      await api.put(`/children/${activeChild.child_id}`, patch);
      updateActiveChild(patch);
      toast.success('Saved');
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  // Iter-51.5: Profile no longer inlines the Data & Privacy / Rewards /
  // Upload guidelines cards — each now lives on its own linked page,
  // matching the /terms pattern. Delete-history moved with Data & Privacy.

  // Parent PIN management (Set / Change).
  const [pinStatus, setPinStatus] = useState({ has_pin: false });
  const [pinEditing, setPinEditing] = useState(false);
  const [pinNew, setPinNew] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  useEffect(() => {
    api.get('/parent/pin/status').then(({data}) => setPinStatus(data)).catch(() => {});
  }, []);
  const savePin = async () => {
    if (!PIN_REGEX.test(pinNew)) return toast.error('PIN must be 4-8 characters (letters, numbers, or symbols).');
    if (pinNew !== pinConfirm) return toast.error('Both PINs must match.');
    try {
      await api.post('/parent/pin/set', { pin: pinNew });
      setPinStatus({ has_pin: true, locked_until_seconds: 0 });
      setPinEditing(false); setPinNew(''); setPinConfirm('');
      toast.success('Parent/Guardian PIN saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save PIN'); }
  };

  // Iter-51.35 — Child PIN management for the ACTIVE profile.
  // Set / Change / Remove flows, each Parent-PIN-gated.
  const [childPinStatus, setChildPinStatus] = useState({ has_pin: false });
  const [childPinEditing, setChildPinEditing] = useState(false);
  const [childPinNew, setChildPinNew] = useState('');
  const [childPinConfirm, setChildPinConfirm] = useState('');
  const [parentGateOpen, setParentGateOpen] = useState(false);
  const [gateNextAction, setGateNextAction] = useState(null); // 'edit' | 'remove'

  useEffect(() => {
    if (!activeChild?.child_id) return;
    api.get(`/children/${activeChild.child_id}/pin/status`)
      .then(({ data }) => setChildPinStatus(data))
      .catch(() => {});
  }, [activeChild?.child_id]);

  // Deep-link from ChildPinModal's "Reset" button → open the editor as
  // soon as the parent lands here.
  useEffect(() => {
    if (!activeChild?.child_id) return;
    const stashed = sessionStorage.getItem('goToChildPinSettings');
    if (stashed && stashed === activeChild.child_id) {
      sessionStorage.removeItem('goToChildPinSettings');
      setGateNextAction('edit');
      setParentGateOpen(true);
    }
  }, [activeChild?.child_id]);

  const runAfterParentGate = async () => {
    setParentGateOpen(false);
    const parentToken = getPinUnlockToken();
    if (!parentToken || !activeChild?.child_id) return;
    if (gateNextAction === 'edit') {
      setChildPinEditing(true);
    } else if (gateNextAction === 'remove') {
      try {
        await api.delete(`/children/${activeChild.child_id}/pin`, {
          headers: { 'x-pin-unlock': parentToken },
        });
        setChildPinStatus({ has_pin: false });
        toast.success('Profile PIN removed — anyone with this account can open this profile now.');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not remove PIN');
      }
    }
    setGateNextAction(null);
  };

  const saveChildPin = async () => {
    if (!PIN_REGEX.test(childPinNew)) return toast.error('PIN must be 4-8 characters.');
    if (childPinNew !== childPinConfirm) return toast.error('Both PINs must match.');
    const parentToken = getPinUnlockToken();
    if (!parentToken) { setGateNextAction('edit'); setParentGateOpen(true); return; }
    try {
      await api.post(
        `/children/${activeChild.child_id}/pin/set`,
        { pin: childPinNew },
        { headers: { 'x-pin-unlock': parentToken } },
      );
      setChildPinStatus({ has_pin: true, locked_until_seconds: 0 });
      setChildPinEditing(false); setChildPinNew(''); setChildPinConfirm('');
      toast.success(`Profile PIN saved for ${activeChild.name}. Share it with them so they can open their profile.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save child PIN');
    }
  };

  const onAvatarFile = async (f) => {
    if (!f) return;
    const url = await fileToDataUrl(f);
    await saveChild({ avatar_url: url });
  };

  return (
    <div className="px-6 pt-6 pb-36 max-w-3xl mx-auto space-y-6 overflow-x-hidden">
      <div className="bg-white rounded-3xl p-6" data-testid="parent-info-card">
        <div className="flex items-start gap-4 mb-4">
          {user?.picture && <img src={user.picture} alt="" className="w-16 h-16 rounded-full flex-shrink-0"/>}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase text-slate-500">Parent / Guardian</div>
            <div data-testid="parent-name" className="text-xl font-extrabold break-words">{user?.name || '—'}</div>
            {user?.email && (
              <div data-testid="parent-email" className="text-sm text-slate-500 break-all">{user.email}</div>
            )}
          </div>
          {!parentEditing && (
            <button
              data-testid="parent-edit-btn"
              onClick={() => setParentEditing(true)}
              className="pushable bg-white border-2 border-[#FF6B1A] text-[#FF6B1A] font-bold px-4 py-2 rounded-full inline-flex items-center gap-1 text-sm flex-shrink-0"
            >
              <Pencil size={14} strokeWidth={3}/> Edit
            </button>
          )}
        </div>

        {!parentEditing ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2" data-testid="parent-details">
            <ParentField
              icon={<Phone size={14}/>}
              label="Mobile"
              value={user?.mobile_e164}
              placeholder="Not added"
              testId="parent-mobile"
            />
            <ParentField
              icon={<MapPin size={14}/>}
              label="State"
              value={user?.state}
              placeholder="Not set"
              testId="parent-state"
            />
            <ParentField
              icon={<Building2 size={14}/>}
              label="City"
              value={user?.city}
              placeholder="Not set"
              testId="parent-city"
            />
          </div>
        ) : (
          <div className="mt-2 space-y-3" data-testid="parent-edit-form">
            <div>
              <label className="text-[11px] font-bold uppercase text-slate-500 tracking-wide">Name</label>
              <div className="flex items-center gap-2 mt-1 px-4 rounded-2xl border-2 border-slate-200 bg-white focus-within:border-[#FF6B1A]">
                <UserIcon size={16} className="text-slate-400"/>
                <input
                  data-testid="parent-name-input"
                  value={parentName}
                  onChange={(e) => setParentName(e.target.value)}
                  placeholder="Your name"
                  maxLength={80}
                  className="flex-1 bg-transparent outline-none py-2.5 text-[#2B2140] text-sm"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase text-slate-500 tracking-wide">State</label>
              <div className="flex items-center gap-2 mt-1 px-4 rounded-2xl border-2 border-slate-200 bg-white focus-within:border-[#FF6B1A]">
                <MapPin size={16} className="text-slate-400"/>
                <select
                  data-testid="parent-state-input"
                  value={parentState}
                  onChange={(e) => setParentState(e.target.value)}
                  className="flex-1 bg-transparent outline-none py-2.5 text-[#2B2140] text-sm"
                >
                  <option value="">Select your state</option>
                  {INDIAN_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase text-slate-500 tracking-wide">City</label>
              <div className="flex items-center gap-2 mt-1 px-4 rounded-2xl border-2 border-slate-200 bg-white focus-within:border-[#FF6B1A]">
                <Building2 size={16} className="text-slate-400"/>
                <input
                  data-testid="parent-city-input"
                  value={parentCity}
                  onChange={(e) => setParentCity(e.target.value)}
                  placeholder="Your city"
                  maxLength={80}
                  className="flex-1 bg-transparent outline-none py-2.5 text-[#2B2140] text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                data-testid="parent-save-btn"
                onClick={saveParentProfile}
                disabled={parentSaving}
                className="pushable bg-[#FF6B1A] text-white font-bold px-5 py-2 rounded-full text-sm disabled:opacity-60"
              >
                {parentSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                data-testid="parent-cancel-btn"
                onClick={() => {
                  setParentEditing(false);
                  setParentName(user?.name || '');
                  setParentState(user?.state || '');
                  setParentCity(user?.city || '');
                }}
                className="bg-slate-100 text-slate-600 font-bold px-5 py-2 rounded-full text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Iter-52.32 — Mobile add flow. Only shown when the user does
            NOT already have a verified mobile (typical for Google
            signups before the OTP gate). Once set, mobile is
            read-only here — a rotation flow needs to be handled via
            support to preserve trial-ledger integrity. */}
        {!parentEditing && !user?.mobile_e164 && !addingMobile && (
          <button
            data-testid="parent-add-mobile-btn"
            onClick={() => { setAddingMobile(true); setMobileWidgetKey(k => k + 1); setMobileHintAcked(false); }}
            className="pushable mt-4 w-full bg-[#25D366] text-white text-sm font-bold px-5 py-2.5 rounded-full inline-flex items-center justify-center gap-2"
          >
            <ShieldCheck size={14} strokeWidth={2.8}/> Add your mobile number
          </button>
        )}
        {addingMobile && !user?.mobile_e164 && (
          <div className="mt-4" data-testid="parent-mobile-widget-wrap">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase text-slate-500">Verify your mobile</div>
              <button
                data-testid="parent-cancel-mobile"
                onClick={() => setAddingMobile(false)}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Cancel mobile verification"
              >
                <X size={16}/>
              </button>
            </div>
            {/* Iter-52.39/41 — bilingual WhatsApp hint + ack gate (see Register.jsx). */}
            <div
              data-testid="whatsapp-hint"
              className="mb-3 px-4 py-3 rounded-2xl bg-[#25D366]/10 border-2 border-[#25D366]/40 text-[14px] text-[#0F5132] leading-snug"
            >
              <div className="font-extrabold flex items-start gap-2">
                <span className="text-lg leading-none">📱</span>
                <span>Please enter your <span className="underline decoration-2 decoration-[#25D366] underline-offset-2">WhatsApp number</span></span>
              </div>
              <div className="text-[12px] text-[#0F5132]/85 font-hi mt-1 ml-6">कृपया अपना WhatsApp नंबर दर्ज करें</div>
              <div className="text-[11px] text-[#0F5132]/70 mt-1.5 ml-6">
                We'll send OTP + trial reminders on WhatsApp.
              </div>
            </div>
            {!mobileHintAcked ? (
              <button
                data-testid="whatsapp-hint-ack"
                onClick={() => setMobileHintAcked(true)}
                className="pushable w-full bg-[#25D366] hover:bg-[#20BF5B] text-white text-base font-bold py-3 rounded-full inline-flex items-center justify-center gap-2"
              >
                I got it — verify my WhatsApp number
              </button>
            ) : (
              <Msg91Widget
                key={mobileWidgetKey}
                onVerified={onMobileVerified}
                onError={(m) => toast.error(m || 'Mobile verification failed.')}
                label={mobileVerifying ? 'Adding to your account…' : 'Enter your number'}
              />
            )}
          </div>
        )}
      </div>

      {activeChild && (
        <>
          <div className="bg-white rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-slate-100 flex-shrink-0">
                {activeChild.avatar_url ? <img src={activeChild.avatar_url} alt="" className="w-full h-full object-cover"/> :
                  <div className="w-full h-full flex items-center justify-center text-2xl">{activeChild.avatar==='bear'?'🐻':'🦌'}</div>}
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold uppercase text-slate-500">Reading as</div>
                <div className="text-xl font-extrabold flex items-center gap-2 flex-wrap">
                  {activeChild.name}
                  {activeChild.sparky_slayer && (
                    <span data-testid="profile-sparky-slayer-badge"
                      className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#F4B740] text-white text-[10px] font-extrabold px-2 py-0.5 whitespace-nowrap"
                      title={activeChild.sparky_slayer_at ? `Earned ${new Date(activeChild.sparky_slayer_at).toLocaleDateString(undefined, {day:'numeric', month:'short', year:'numeric'})}` : 'Sparky-slayer'}>
                      🏆 Sparky-slayer
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-600">{activeChild.languages?.join(', ').toUpperCase()}</div>
              </div>
              <label data-testid="change-avatar-btn" className="pushable bg-slate-100 font-bold px-3 py-2 rounded-full text-sm inline-flex items-center gap-1 cursor-pointer">
                <Camera size={16} strokeWidth={3}/> {t('customAvatar')}
                <input type="file" accept="image/*" onChange={e => onAvatarFile(e.target.files?.[0])} className="hidden"/>
              </label>
            </div>
            <button data-testid="switch-profile-btn" onClick={() => { clearChild(); nav('/profiles'); }} className="pushable bg-[#F4B740] text-[#FF6B1A] font-bold px-5 py-3 rounded-full inline-flex items-center gap-2 text-sm">
              <Users size={16} strokeWidth={3}/> {t('switchProfile')}
            </button>
            <button data-testid="edit-profile-btn" onClick={() => setEditingProfile(true)} className="pushable bg-white border-2 border-[#FF6B1A] text-[#FF6B1A] font-bold px-5 py-3 rounded-full inline-flex items-center gap-2 text-sm ml-2">
              <Pencil size={16} strokeWidth={3}/> Edit profile
            </button>
            {editingProfile && (
              <ChildForm
                initial={activeChild}
                onClose={() => setEditingProfile(false)}
                onSaved={(next) => {
                  setEditingProfile(false);
                  if (next) updateActiveChild(next);
                  toast.success('Profile updated');
                }}
              />
            )}
          </div>

          <div className="bg-white rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Volume2 size={22} strokeWidth={3} className="text-slate-500"/>
              <div className="text-lg font-extrabold">{t('voice')}</div>
            </div>
            <div className="flex gap-2 flex-wrap mb-4">
              <button data-testid="profile-voice-female" onClick={() => { setGender('female'); saveChild({ voice_gender: 'female' }); }}
                className={`px-4 py-2 rounded-full font-bold border ${gender==='female'?'bg-[#FF6B1A] text-white border-[#FF6B1A]':'bg-white text-[#2B2140] border-slate-200'}`}>{t('voiceFemale')}</button>
              <button data-testid="profile-voice-male" onClick={() => { setGender('male'); saveChild({ voice_gender: 'male' }); }}
                className={`px-4 py-2 rounded-full font-bold border ${gender==='male'?'bg-[#FF6B1A] text-white border-[#FF6B1A]':'bg-white text-[#2B2140] border-slate-200'}`}>{t('voiceMale')}</button>
            </div>
            <div className="mb-2">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-bold uppercase text-slate-500">{t('readingSpeed')}</span>
                <span data-testid="profile-speed-value" className="text-sm font-bold text-[#FF6B1A]">{speed.toFixed(2)}x</span>
              </div>
              {/* Iter-51.97 — slider goes to 0.5x. The named tier
                  "Very slow" lives at 0.55x (the preset button in
                  ChildForm); the slider lets parents override to any
                  precise value between 0.5 and 1.2. */}
              <input data-testid="profile-speed-slider" type="range" min={0.5} max={1.2} step={0.05} value={speed}
                onChange={e => setSpeed(Number(e.target.value))}
                onMouseUp={e => saveChild({ tts_speed: Number(e.target.value) })}
                onTouchEnd={e => saveChild({ tts_speed: Number(e.target.value) })}
                className="w-full accent-[#FF6B1A]"/>
              <div className="flex justify-between text-[10px] font-bold uppercase text-slate-400 mt-1">
                <span>0.5x · very slow</span>
                <span>1.2x · fast</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Iter-51.35 — Per-profile PIN card. Only shown when a profile is
          active (i.e., you're viewing an actual child profile). Locking
          each profile with its own PIN keeps sibling content separate. */}
      {activeChild && (
        <div data-testid="child-pin-card" className="bg-white rounded-3xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-[#5B8DEF] text-white flex items-center justify-center">
              <Lock size={20} strokeWidth={2.8}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-lg font-extrabold">Profile access PIN</div>
              <div className="text-xs text-slate-500 leading-relaxed">
                Locks {activeChild.name}&apos;s books, points and history so a sibling can&apos;t open this profile. Only you can set or reset it.
              </div>
            </div>
          </div>
          {!childPinEditing ? (
            <div className="mt-3">
              <div data-testid="child-pin-status-line" className={`text-sm font-bold ${childPinStatus.has_pin ? 'text-[#4A9B7F]' : 'text-[#B45309]'}`}>
                {childPinStatus.has_pin
                  ? `✓ PIN is active on ${activeChild.name}'s profile`
                  : `⚠ Not set — anyone with this account can open ${activeChild.name}'s profile`}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  data-testid="child-pin-edit-btn"
                  onClick={() => { setGateNextAction('edit'); setParentGateOpen(true); }}
                  className="pushable bg-[#5B8DEF] text-white font-bold px-5 py-2 rounded-full text-sm inline-flex items-center gap-1.5"
                >
                  <Lock size={14} strokeWidth={3}/>
                  {childPinStatus.has_pin ? 'Change PIN' : 'Set PIN'}
                </button>
                {childPinStatus.has_pin && (
                  <button
                    data-testid="child-pin-remove-btn"
                    onClick={() => {
                      if (!window.confirm(`Remove the PIN on ${activeChild.name}'s profile? Anyone with this account will be able to open it.`)) return;
                      setGateNextAction('remove');
                      setParentGateOpen(true);
                    }}
                    className="bg-slate-100 text-slate-700 font-bold px-4 py-2 rounded-full text-sm inline-flex items-center gap-1.5"
                  >
                    <Unlock size={14} strokeWidth={3}/> Remove PIN
                  </button>
                )}
              </div>
              <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
                Forgot the PIN? Tap Change PIN — with your Parent/Guardian PIN you can always set a fresh one.
              </p>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <PinInput testId="child-pin-new" value={childPinNew} onChange={setChildPinNew} placeholder="New PIN (4-8)"/>
              <PinInput testId="child-pin-confirm" value={childPinConfirm} onChange={setChildPinConfirm} placeholder="Confirm PIN"/>
              <div className="flex gap-2">
                <button data-testid="child-pin-save" onClick={saveChildPin} className="pushable bg-[#5B8DEF] text-white font-bold px-5 py-2 rounded-full text-sm">Save</button>
                <button
                  onClick={() => { setChildPinEditing(false); setChildPinNew(''); setChildPinConfirm(''); }}
                  className="bg-slate-100 text-slate-600 font-bold px-5 py-2 rounded-full text-sm">
                  Cancel
                </button>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Share this PIN with {activeChild.name} directly, outside the app. They&apos;ll type it every new browser session to open their profile.
              </p>
            </div>
          )}
        </div>
      )}

      <div data-testid="parent-pin-card" className="bg-white rounded-3xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-[#FF6B1A] text-white flex items-center justify-center">
            <KeyRound size={20} strokeWidth={2.8}/>
          </div>
          <div>
            <div className="text-lg font-extrabold">Parent / Guardian PIN</div>
            <div className="text-xs text-slate-500">Gates the Add-Book / camera flow only</div>
          </div>
        </div>
        {!pinEditing ? (
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-slate-600">
              {pinStatus.has_pin ? 'PIN is set. You can change it anytime.' : 'No PIN yet — set one to protect the camera flow.'}
            </div>
            <button data-testid="parent-pin-edit-btn" onClick={() => setPinEditing(true)}
              className="pushable bg-[#FF6B1A] text-white font-bold px-5 py-2 rounded-full text-sm">
              {pinStatus.has_pin ? 'Change PIN' : 'Set PIN'}
            </button>
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            <PinInput testId="parent-pin-new" value={pinNew} onChange={setPinNew} placeholder="New PIN (4-8)"/>
            <PinInput testId="parent-pin-confirm" value={pinConfirm} onChange={setPinConfirm} placeholder="Confirm PIN"/>
            <div className="flex gap-2">
              <button data-testid="parent-pin-save" onClick={savePin} className="pushable bg-[#FF6B1A] text-white font-bold px-5 py-2 rounded-full text-sm">Save</button>
              <button onClick={() => { setPinEditing(false); setPinNew(''); setPinConfirm(''); }} className="bg-slate-100 text-slate-600 font-bold px-5 py-2 rounded-full text-sm">Cancel</button>
            </div>
            <p className="text-xs text-slate-500">
              Forgot? Sign out and sign in again with Google, then set a new PIN.
            </p>
          </div>
        )}
      </div>

      {/* Iter-51.5: Guides & info moved OUT of Profile into their own
          linked pages so this screen stops scrolling forever. Each link
          renders as a compact card matching the /terms row visual. */}
      <div data-testid="profile-guides" className="bg-white rounded-3xl overflow-hidden">
        <div className="px-6 pt-5 pb-3">
          <div className="text-lg font-extrabold">Guides &amp; info</div>
          <div className="text-xs text-slate-500 mt-0.5">Everything you might want to know, on its own page.</div>
        </div>
        <ProfileLink to="/how-to-use" testId="link-how-to-use"
          icon={<BookOpenText size={18} strokeWidth={2.8}/>} iconBg="bg-[#FF6B1A]"
          title="How to use the app"
          subtitle="Step-by-step tour of every reading action" />
        {/* Iter-51.10 — bilingual "Who is this for?" card. Sits right
            after "How to use" because parents naturally ask this next. */}
        <ProfileLink to="/who-is-this-for" testId="link-who-is-this-for"
          icon={<HeartHandshake size={18} strokeWidth={2.8}/>} iconBg="bg-[#B85582]"
          title="Who is this for?"
          subtitle="कौन-कौन इस्तेमाल कर सकता है — kids, teens, adults" />
        <ProfileLink to="/rewards" testId="link-rewards"
          icon={<Sparkles size={18} strokeWidth={2.8}/>} iconBg="bg-[#F4B740]"
          title="Stars, streaks &amp; rewards"
          subtitle="How reading turns into celebrations" />
        <ProfileLink to="/upload-guidelines" testId="link-upload-guidelines"
          icon={<FileText size={18} strokeWidth={2.8}/>} iconBg="bg-[#5B8DEF]"
          title="Upload guidelines"
          subtitle="What can and can't be added to your library" />
        <ProfileLink to="/data-privacy" testId="link-data-privacy"
          icon={<FileCheck2 size={18} strokeWidth={2.8}/>} iconBg="bg-slate-800"
          title="Data &amp; privacy"
          subtitle="How this app handles your data" />
        <ProfileLink to="/help-support" testId="link-help-support"
          icon={<LifeBuoy size={18} strokeWidth={2.8}/>} iconBg="bg-[#4A9B7F]"
          title="Help &amp; support"
          subtitle="Message the team — usually replies within a day" />
      </div>

      {/* Iter-51.49 — App-language card, AI-scoring notice, trial
          sessions, and Beat the Bot explainer all moved OUT of
          Profile into the guide pages (HowToUse intro + step 10c,
          DataPrivacy disclaimer, Rewards streak-explainer trial row).
          Profile is now a settings-only surface again — the
          how-it-works content lives one tap away in Guides & info,
          where it also gets the shared EN/HI toggle. */}

      <button data-testid="profile-logout-btn" onClick={async () => { await logout(); nav('/'); }} className="bg-red-50 text-[#E8435C] font-bold px-6 py-4 rounded-full inline-flex items-center gap-2">
        <LogOut size={20} strokeWidth={3}/> {t('logout')}
      </button>

      {/* Iter-51.35 — Parent PIN gate that fronts the child-PIN edit /
          remove flows. Opens on tap of "Set/Change/Remove PIN" above. */}
      {parentGateOpen && (
        <PinGateModal
          context="child-override"
          onUnlocked={runAfterParentGate}
          onCancel={() => { setParentGateOpen(false); setGateNextAction(null); }}
        />
      )}
    </div>
  );
}

function ParentField({ icon, label, value, placeholder, testId }) {
  const filled = value && String(value).trim();
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        <span className="text-slate-400">{icon}</span> {label}
      </div>
      <div
        data-testid={testId}
        className={`mt-0.5 text-sm font-bold break-words ${filled ? 'text-[#2B2140]' : 'text-slate-400 italic'}`}
      >
        {filled ? value : placeholder}
      </div>
    </div>
  );
}

function ProfileLink({ to, testId, icon, iconBg, title, subtitle }) {
  return (
    <Link data-testid={testId} to={to}
      className="flex items-center gap-3 px-6 py-4 border-t border-slate-100 hover:bg-slate-50 transition-colors">
      <div className={`w-9 h-9 rounded-2xl ${iconBg} text-white flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-extrabold text-[#2B2140] leading-tight">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">{subtitle}</div>
      </div>
      <ChevronRight size={18} className="text-slate-400 shrink-0" />
    </Link>
  );
}
